import { makeStyles } from "tss-react/mui";
import { toKebabCase } from "@miplatform/utils";
import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import { MiButton } from "../MiButton/MiButton";
import { MiIcon } from "../MiIcon/MiIcon";

/**
 * A flexible card component with customizable header, icons, and content area.
 *
 * @param {Object} props - The component props
 * @param {string} [props.leftIcon] - Icon path for the left icon in a circular background
 * @param {React.ReactNode} [props.customLeftIcon] - Custom left icon component to override the default leftIcon
 * @param {string} [props.rightIcon] - Icon path for the right icon button
 * @param {string} props.title - The main title text displayed in the card header
 * @param {React.ReactNode} [props.children] - Content to be rendered in the card body
 * @param {Function} [props.onRightIconClick] - Click handler for the right icon button
 * @param {React.ReactNode} [props.rightComponent] - Custom component to display on the right side of the header
 * @param {string} [props.rightIconHoverText] - Tooltip text for the right icon button
 * @param {boolean} [props.rightIconDisabled=false] - Whether the right icon button is disabled
 * @param {string} [props.titleColor] - Custom color for the title text
 * @param {Object} [props.containerStyles={ margin: 16, padding: 12 }] - Custom styles for the main container
 * @param {Object} [props.headerContainerStyles] - Custom styles for the header container
 * @param {Object} [props.titleStyles] - Custom styles for the title text
 * @param {boolean} [props.inverse] - Whether to use inverse color scheme (dark background with light text)
 * @param {string} [props.subtitle] - Optional subtitle text displayed below the title
 * @param {string} [props.description] - Optional description text displayed below the header
 * @param {Object} [props.rest] - Additional props passed to the root Box component
 * @param {Object} [props.className] - Additional className for the root Box component
 * @returns {JSX.Element} The rendered MiCard component
 */
export function MiCard({
  leftIcon,
  customLeftIcon,
  rightIcon,
  title,
  children,
  onRightIconClick,
  rightComponent,
  rightIconHoverText,
  rightIconDisabled,
  titleColor,
  containerStyles = { margin: 16, padding: 12 },
  headerContainerStyles,
  titleStyles,
  inverse,
  subtitle,
  description,
  className,
  ...rest
}) {
  const { classes } = useStyles({
    containerStyles,
    headerContainerStyles,
    inverse,
  });
  const theme = useTheme();

  return (
    <Box className={[classes.sectionContainer, className]} {...rest}>
      <Box className={classes.header}>
        <Box>
          <Box className={classes.headerLeft}>
            {customLeftIcon}
            {leftIcon && (
              <Box className={classes.iconCircle}>
                <MiIcon
                  path={leftIcon}
                  size={0.5}
                  color={
                    inverse
                      ? theme.palette.primary.main
                      : theme.palette.common.white
                  }
                />
              </Box>
            )}
            <Typography
              fontWeight={theme.fontWeight.semiBold}
              variant="subtitle2"
              className={classes.title}
              style={{ color: titleColor, ...titleStyles }}
            >
              {title}
            </Typography>
            <Typography variant="caption" className={classes.subtitle}>
              {subtitle}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption">{description}</Typography>
          </Box>
        </Box>
        {rightComponent}
        {rightIcon && (
          <Tooltip title={rightIconHoverText} placement="right">
            <Box>
              <MiButton
                variant="text"
                startIcon={{
                  path: rightIcon,
                  iconSize: 0.8,
                }}
                onClick={onRightIconClick}
                disabled={rightIconDisabled ?? false}
                data-testid={`mi-section-right-icon-${toKebabCase(title)}`}
              />
            </Box>
          </Tooltip>
        )}
      </Box>
      {children}
    </Box>
  );
}

const useStyles = makeStyles()(
  (theme, { containerStyles, headerContainerStyles, inverse }) => ({
    sectionContainer: {
      backgroundColor: inverse
        ? theme.palette.primary.main
        : theme.palette.grey[100],
      borderRadius: 10,
      ...containerStyles,
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: theme.spacing(2),
      ...headerContainerStyles,
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
    },
    iconCircle: {
      height: 18,
      width: 18,
      backgroundColor: inverse
        ? theme.palette.common.white
        : theme.palette.primary.main,
      borderRadius: 18,
      justifyContent: "center",
      display: "flex",
      alignItems: "center",
    },
    title: {
      color: inverse ? theme.palette.common.white : theme.palette.common.black,
    },
    subtitle: {
      color: inverse ? theme.palette.common.white : theme.palette.common.black,
    },
  })
);
